import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { videos } from "@/mocks/videos";
import { categories } from "@/mocks/categories";
import { durationBucket } from "@/lib/video";
import VideoCard from "@/components/feature/VideoCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import FilterSidebar, { type CategoryFilters } from "./components/FilterSidebar";

const PAGE_SIZE = 9;
const TODAY = "2026-10-13";

const defaultFilters: CategoryFilters = {
  time: "all",
  duration: "all",
  sort: "latest",
};

export default function CategoryPage() {
  const { categorySlug = "" } = useParams();
  const category = categories.find((c) => c.slug === categorySlug);

  const [filters, setFilters] = useState<CategoryFilters>(defaultFilters);
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    setFilters(defaultFilters);
    setPage(1);
  }, [categorySlug]);

  const result = useMemo(() => {
    let list = videos.filter((v) => v.categorySlug === categorySlug);

    if (filters.time === "latest") {
      const maxDate = [...list].sort((a, b) =>
        b.publishDate.localeCompare(a.publishDate),
      )[0]?.publishDate;
      if (maxDate) list = list.filter((v) => v.publishDate === maxDate);
    } else if (filters.time === "week") {
      const limit = new Date(TODAY).getTime() - 7 * 86400000;
      list = list.filter((v) => new Date(v.publishDate).getTime() >= limit);
    } else if (filters.time === "month") {
      const limit = new Date(TODAY).getTime() - 30 * 86400000;
      list = list.filter((v) => new Date(v.publishDate).getTime() >= limit);
    }

    if (filters.duration !== "all") {
      list = list.filter((v) => durationBucket(v.duration) === filters.duration);
    }

    if (filters.sort === "hot") list.sort((a, b) => b.views - a.views);
    else if (filters.sort === "likes") list.sort((a, b) => b.likes - a.likes);
    else list.sort((a, b) => b.publishDate.localeCompare(a.publishDate));

    return list;
  }, [categorySlug, filters]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const pageItems = result.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleFilterChange = (next: CategoryFilters) => {
    setFilters(next);
    setPage(1);
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    setPage(1);
  };

  if (!category) {
    return (
      <div className="px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-error-warning-line"
          title="未找到该分类"
          description="你访问的分类不存在或已下线，可以返回首页看看其他热门内容。"
          actions={[
            { label: "返回首页", to: "/" },
            { label: "浏览热门视频", to: "/category/funny", variant: "ghost" },
          ]}
        />
      </div>
    );
  }

  return (
    <div className="px-4 py-8 md:px-6 md:py-10">
      <div className="mb-4">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "分类", to: "/" },
            { label: category.name },
          ]}
        />
      </div>

      <div className="mb-8 flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600 md:h-16 md:w-16">
          <i className={`${category.icon} text-3xl`}></i>
        </span>
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            {category.name}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            {category.desc}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr] lg:gap-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
            <FilterSidebar
              value={filters}
              onChange={handleFilterChange}
              onReset={handleReset}
            />
          </div>
        </aside>

        <div>
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="text-sm text-foreground-500">
              共 <span className="font-label text-foreground-900">{result.length}</span> 个视频
            </p>
            <button
              type="button"
              onClick={() => setMobileFilterOpen((v) => !v)}
              className="flex items-center gap-1 rounded-md border border-background-200 bg-background-100 px-3 py-2 text-sm font-label text-foreground-700 lg:hidden cursor-pointer whitespace-nowrap"
            >
              <i className="ri-filter-3-line text-lg"></i>
              筛选
            </button>
          </div>

          {mobileFilterOpen && (
            <div className="mb-6 rounded-lg border border-background-200 bg-background-50 p-5 lg:hidden">
              <FilterSidebar
                value={filters}
                onChange={handleFilterChange}
                onReset={handleReset}
              />
            </div>
          )}

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 md:gap-4">
                {pageItems.map((v) => (
                  <VideoCard key={v.id} video={v} showCategory={false} />
                ))}
              </div>
              <Pagination current={page} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <EmptyState
              title="未找到相关视频"
              description="当前筛选条件下没有匹配的短视频，试试重置筛选条件，或返回首页浏览其他内容。"
              actions={[
                { label: "重置筛选", onClick: handleReset },
                { label: "返回首页", to: "/", variant: "ghost" },
              ]}
            />
          )}

          <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="mb-4 font-heading text-base font-bold text-foreground-950">
              其他分类
            </h2>
            <div className="flex flex-wrap gap-2">
              {categories
                .filter((c) => c.slug !== category.slug)
                .map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="inline-flex items-center gap-2 rounded-full bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors duration-200 hover:bg-primary-100 hover:text-primary-700"
                  >
                    <i className={`${c.icon} text-base text-primary-500`}></i>
                    {c.name}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}